import React, { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import AuthLayout from '../components/AuthLayout';
import TextInput from '../components/TextInput';
import Button from '../components/Button';
import ErrorBanner from '../components/ErrorBanner';
import { resendVerification, verifyEmail } from '../api/authApi';
import { ApiError } from '../types/auth.types';
import forgotStyles from './ForgotPasswordPage.module.css';
import styles from './VerifyEmailPage.module.css';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [verifying, setVerifying] = useState(true);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }
    verifyEmail(token)
      .then((response) => setSuccessMessage(response.mensaje))
      .catch((error) => {
        const data = axios.isAxiosError<ApiError>(error) ? error.response?.data : undefined;
        setErrorMessage(data?.mensaje ?? 'El enlace de verificación no es válido o ha expirado.');
      })
      .finally(() => setVerifying(false));
  }, [token, navigate]);

  async function handleResend(event: FormEvent) {
    event.preventDefault();
    if (!email.trim()) return;
    setResending(true);
    setResendError(null);
    try {
      await resendVerification(email.trim());
      setResendSent(true);
    } catch {
      setResendError('Ocurrió un error. Intentá nuevamente.');
    } finally {
      setResending(false);
    }
  }

  if (!token) {
    return null;
  }

  if (verifying) {
    return (
      <AuthLayout title="Confirmar cuenta" logoSize={150}>
        <p className={styles.info}>Verificando tu email...</p>
      </AuthLayout>
    );
  }

  if (successMessage) {
    return (
      <AuthLayout title="Confirmar cuenta" logoSize={150}>
        <div className={forgotStyles.successState}>
          <svg
            viewBox="0 0 24 24"
            width="40"
            height="40"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className={forgotStyles.envelopeIcon}
          >
            <path d="M20 6L9 17l-5-5" />
          </svg>
          <p className={forgotStyles.successText}>{successMessage}</p>
          <Link to="/login" className={forgotStyles.backLink}>
            Ir a iniciar sesión
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Enlace inválido" logoSize={150}>
      <p className={styles.errorText}>{errorMessage}</p>

      {resendSent ? (
        <p className={styles.info}>
          Si el email existe y todavía no fue confirmado, vas a recibir un nuevo enlace.
        </p>
      ) : (
        <form onSubmit={handleResend} noValidate>
          <p className={forgotStyles.description}>
            Ingresá tu email para recibir un nuevo enlace de confirmación.
          </p>
          {resendError && <ErrorBanner message={resendError} onClose={() => setResendError(null)} />}
          <TextInput
            id="email"
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            autoComplete="email"
          />
          <Button type="submit" loading={resending} loadingText="Enviando...">
            Reenviar enlace
          </Button>
        </form>
      )}

      <div className={forgotStyles.backLinkWrapper}>
        <Link to="/login" className={forgotStyles.backLink}>
          Volver al inicio de sesión
        </Link>
      </div>
    </AuthLayout>
  );
}
